import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type {
  PersonalCurriculum,
  PersonalCurriculumStep,
  CurriculumTemplate,
  CurriculumTemplateDetail,
  CampusCurrentWeekResponse,
  BuildCurriculumPayload,
  BuildCurriculumResponse,
  BySectionResponse,
  ReorderStepsPayload,
  AddPersonalStepPayload,
  CreateStepReviewPayload,
  StepReview,
  PersonalProgressStatus,
  FellowProgressSummary,
  GuideUpdateStepPayload,
  PreviousProgramSummary,
  StartNewProgramOptions,
  StartNewProgramPayload,
  StartNewProgramResponse,
} from '@/types/campus'

export interface GuideFellowDisplay {
  displayName: string
  avatarUrl: string | null
}

export const useCampusStore = defineStore('campus', () => {
  const personalCurriculum = ref<PersonalCurriculum | null>(null)
  const planSteps = ref<PersonalCurriculumStep[]>([])
  const templates = ref<CurriculumTemplate[]>([])
  const currentWeekData = ref<CampusCurrentWeekResponse | null>(null)
  const sectionSteps = ref<Record<number, PersonalCurriculumStep[]>>({})
  const hasPersonalCurriculum = ref<boolean | null>(null)
  const isLoading = ref(false)
  const isBuilding = ref(false)
  const isCommitting = ref(false)
  const isReordering = ref(false)
  const error = ref<string | null>(null)

  /** When set, the store operates in guide mode — all API calls target this fellow's curriculum */
  const targetUserId = ref<string | null>(null)

  /** Resolved name + avatar for the guide campus bar (from fellows list API) */
  const guideFellowDisplay = ref<GuideFellowDisplay | null>(null)
  const guideFellowDisplayLoading = ref(false)

  function clearGuideFellowDisplay() {
    guideFellowDisplay.value = null
    guideFellowDisplayLoading.value = false
  }

  async function fetchGuideFellowDisplay(
    userId: string,
    podId?: string,
    fallbackName?: string,
  ) {
    guideFellowDisplay.value = {
      displayName: fallbackName?.trim() || `User ${userId}`,
      avatarUrl: null,
    }
    guideFellowDisplayLoading.value = true
    try {
      const params = podId ? { pod_id: podId } : undefined
      const { data } = await api.get<FellowProgressSummary[]>(API_ROUTES.CAMPUS_GUIDE_FELLOWS, {
        params,
      })
      const row = data.find((f) => f.user_id === Number(userId))
      if (row) {
        const displayName =
          `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || `User ${userId}`
        guideFellowDisplay.value = {
          displayName,
          avatarUrl: row.avatar_image ?? null,
        }
      } else {
        guideFellowDisplay.value = {
          displayName: fallbackName?.trim() || `User ${userId}`,
          avatarUrl: null,
        }
      }
    } catch {
      guideFellowDisplay.value = {
        displayName: fallbackName?.trim() || `User ${userId}`,
        avatarUrl: null,
      }
    } finally {
      guideFellowDisplayLoading.value = false
    }
  }

  function setGuideTarget(userId: string | null) {
    if (targetUserId.value !== userId) {
      // Availability flags (can_add_to_curriculum) are per-user; force refetch on target change
      templates.value = []
    }
    targetUserId.value = userId
    if (!userId) {
      clearGuideFellowDisplay()
    }
  }

  /** Returns the right curriculum URL depending on guide mode */
  function curriculumUrl() {
    return targetUserId.value
      ? API_ROUTES.CAMPUS_GUIDE_FELLOW_CURRICULUM(targetUserId.value)
      : API_ROUTES.CAMPUS_MY_CURRICULUM
  }

  const TRACK_ORDER_KEY = 'campus-track-order'
  const LEGACY_TRACK_ORDER_KEY = 'campus-track-order'

  /** User's preferred track order (template IDs). Persisted to localStorage. Affects task display order within weeks. */
  const trackOrder = ref<number[]>([])
  function loadTrackOrder() {
    try {
      let raw = localStorage.getItem(TRACK_ORDER_KEY)
      const fromLegacy = !raw
      if (!raw) raw = localStorage.getItem(LEGACY_TRACK_ORDER_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as number[]
        if (Array.isArray(parsed)) {
          trackOrder.value = parsed
          if (fromLegacy) {
            localStorage.setItem(TRACK_ORDER_KEY, raw)
            localStorage.removeItem(LEGACY_TRACK_ORDER_KEY)
          }
        }
      }
    } catch {
      trackOrder.value = []
    }
  }
  function saveTrackOrder(ids: number[]) {
    trackOrder.value = ids
    // Only persist to localStorage for current user (not guide mode)
    if (!targetUserId.value) {
      try {
        localStorage.setItem(TRACK_ORDER_KEY, JSON.stringify(ids))
      } catch {
        /* ignore */
      }
    }
    // Persist to backend so Curriculum Plan uses it across sessions/devices
    if (personalCurriculum.value) {
      api
        .patch(curriculumUrl(), { section_order: ids })
        .then(async (res) => {
          personalCurriculum.value = { ...personalCurriculum.value!, ...res.data }
          // Backend redistributes steps by section_order; refetch to get new week assignments
          await fetchFullPlan()
        })
        .catch(() => {
          /* ignore; localStorage fallback remains */
        })
    }
  }

  if (typeof localStorage !== 'undefined') loadTrackOrder()

  const isCommitted = computed(() => personalCurriculum.value?.is_committed ?? false)

  const stepsByWeek = computed(() => {
    const grouped: Record<number, PersonalCurriculumStep[]> = {}
    for (const step of planSteps.value) {
      if (!grouped[step.week_number]) {
        grouped[step.week_number] = []
      }
      grouped[step.week_number].push(step)
    }
    // Prefer: localStorage trackOrder > backend section_order > selected_sections order
    const orderIds =
      trackOrder.value.length > 0
        ? trackOrder.value
        : (personalCurriculum.value?.section_order?.length
            ? personalCurriculum.value.section_order
            : personalCurriculum.value?.selected_sections?.map((s) => s.template) ?? [])
    const orderMap = new Map(orderIds.map((id, i) => [id, i]))
    // Sort by the fellow's manual order first (order_in_week); track order is
    // only a tiebreak. Sorting by track first would snap manual reorders back
    // into track groups. Track priority changes redistribute on the backend.
    for (const week of Object.keys(grouped)) {
      grouped[+week].sort((a, b) => {
        if (a.order_in_week !== b.order_in_week) {
          return a.order_in_week - b.order_in_week
        }
        const aIdx = orderMap.get(a.template ?? 0) ?? 999
        const bIdx = orderMap.get(b.template ?? 0) ?? 999
        if (aIdx !== bIdx) return aIdx - bIdx
        return a.id - b.id
      })
    }
    return grouped
  })

  const totalWeeks = computed(() => {
    const weeks = Object.keys(stepsByWeek.value).map(Number)
    return weeks.length ? Math.max(...weeks) : 0
  })

  const weekPointTotals = computed(() => {
    const totals: Record<number, number> = {}
    for (const [week, steps] of Object.entries(stepsByWeek.value)) {
      totals[+week] = steps.reduce((sum, s) => sum + s.points, 0)
    }
    return totals
  })

  /** Progress per track (template_id): { done, total, percent } for fellows in that track */
  const progressByTemplate = computed(() => {
    const map: Record<number, { done: number; total: number; percent: number }> = {}
    for (const step of planSteps.value) {
      const tid = step.template ?? 0
      if (!map[tid]) map[tid] = { done: 0, total: 0, percent: 0 }
      map[tid].total += 1
      if (step.progress?.status === 'DONE') map[tid].done += 1
    }
    for (const tid of Object.keys(map)) {
      const m = map[+tid]
      m.percent = m.total > 0 ? Math.min((m.done / m.total) * 100, 100) : 0
    }
    return map
  })

  async function checkHasPersonalCurriculum(): Promise<boolean> {
    try {
      await api.get(curriculumUrl())
      hasPersonalCurriculum.value = true
      return true
    } catch (err: any) {
      if (err.response?.status === 404) {
        hasPersonalCurriculum.value = false
        return false
      }
      hasPersonalCurriculum.value = null
      throw err
    }
  }

  async function fetchTemplates() {
    isLoading.value = true
    error.value = null
    try {
      // In guide mode, availability (can_add_to_curriculum) is computed for the target fellow
      const params = targetUserId.value ? { user_id: targetUserId.value } : undefined
      const response = await api.get(API_ROUTES.CAMPUS_TEMPLATES, { params })
      templates.value = response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch templates'
      console.error('Error fetching templates:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchTemplateDetail(id: number | string): Promise<CurriculumTemplateDetail | null> {
    isLoading.value = true
    error.value = null
    try {
      const params = targetUserId.value ? { user_id: targetUserId.value } : undefined
      const response = await api.get(API_ROUTES.CAMPUS_TEMPLATE_DETAIL(id), { params })
      return response.data as CurriculumTemplateDetail
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch template detail'
      console.error('Error fetching template detail:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function buildCurriculum(payload: BuildCurriculumPayload) {
    isBuilding.value = true
    error.value = null
    try {
      const url = targetUserId.value
        ? API_ROUTES.CAMPUS_GUIDE_BUILD_CURRICULUM(targetUserId.value)
        : API_ROUTES.CAMPUS_BUILD
      const response = await api.post(url, payload)
      const data = response.data as BuildCurriculumResponse
      personalCurriculum.value = data.curriculum
      planSteps.value = data.steps
      hasPersonalCurriculum.value = true
      return data
    } catch (err: any) {
      error.value = err.response?.data?.error || err.response?.data?.message || 'Failed to build curriculum'
      console.error('Error building curriculum:', err)
      throw err
    } finally {
      isBuilding.value = false
    }
  }

  async function fetchMyCurriculum() {
    isLoading.value = true
    error.value = null
    try {
      const response = await api.get(curriculumUrl())
      const data = response.data as PersonalCurriculum
      personalCurriculum.value = data
      hasPersonalCurriculum.value = true
      // Sync trackOrder from backend when localStorage is empty (only for current user)
      if (!targetUserId.value && trackOrder.value.length === 0 && data.selected_sections?.length) {
        const ids =
          data.section_order?.length
            ? data.section_order
            : data.selected_sections.map((s) => s.template)
        if (ids.length) {
          trackOrder.value = ids
          try {
            localStorage.setItem(TRACK_ORDER_KEY, JSON.stringify(ids))
          } catch {
            /* ignore */
          }
        }
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        hasPersonalCurriculum.value = false
      } else {
        error.value = err.response?.data?.message || 'Failed to fetch curriculum'
        console.error('Error fetching curriculum:', err)
      }
    } finally {
      isLoading.value = false
    }
  }

  async function fetchFullPlan() {
    isLoading.value = true
    error.value = null
    try {
      const currResponse = await api.get(curriculumUrl())
      personalCurriculum.value = currResponse.data as PersonalCurriculum
      hasPersonalCurriculum.value = true

      const stepsUrl = targetUserId.value
        ? API_ROUTES.CAMPUS_GUIDE_FELLOW_ALL_STEPS(targetUserId.value)
        : API_ROUTES.CAMPUS_ALL_STEPS
      const stepsResponse = await api.get(stepsUrl)
      const allSteps = (stepsResponse.data as { steps: PersonalCurriculumStep[] }).steps
      planSteps.value = allSteps

      // Populate sectionSteps for compatibility (group by template)
      const bySection: Record<number, PersonalCurriculumStep[]> = {}
      for (const step of allSteps) {
        const tid = step.template ?? 0
        if (!bySection[tid]) bySection[tid] = []
        bySection[tid].push(step)
      }
      sectionSteps.value = bySection
    } catch (err: any) {
      if (err.response?.status === 404) {
        hasPersonalCurriculum.value = false
      } else {
        error.value = err.response?.data?.message || 'Failed to fetch full plan'
        console.error('Error fetching full plan:', err)
      }
    } finally {
      isLoading.value = false
    }
  }

  async function fetchCurrentWeek() {
    isLoading.value = true
    error.value = null
    try {
      const response = await api.get(API_ROUTES.CAMPUS_CURRENT_WEEK)
      currentWeekData.value = response.data as CampusCurrentWeekResponse
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch current week'
      console.error('Error fetching current week:', err)
    } finally {
      isLoading.value = false
    }
  }

  /** Add a new track to existing curriculum. Steps distributed after last week. Existing progress untouched. */
  async function addTrack(templateId: number) {
    error.value = null
    try {
      const url = targetUserId.value
        ? API_ROUTES.CAMPUS_GUIDE_ADD_TRACK(targetUserId.value)
        : API_ROUTES.CAMPUS_ADD_TRACK
      const response = await api.post(url, { template_id: templateId })
      const data = response.data as { steps: PersonalCurriculumStep[] }
      planSteps.value = [...planSteps.value, ...data.steps]
      await fetchMyCurriculum()
      return data.steps
    } catch (err: any) {
      error.value = err.response?.data?.error || err.response?.data?.message || 'Failed to add track'
      console.error('Error adding track:', err)
      throw err
    }
  }

  /** Remove a track from curriculum. Deletes all steps and progress for that template only. */
  async function removeTrack(templateId: number) {
    error.value = null
    try {
      const url = targetUserId.value
        ? API_ROUTES.CAMPUS_GUIDE_REMOVE_TRACK(targetUserId.value)
        : API_ROUTES.CAMPUS_REMOVE_TRACK
      await api.post(url, { template_id: templateId })
      planSteps.value = planSteps.value.filter((s) => s.template !== templateId)
      await fetchMyCurriculum()
    } catch (err: any) {
      error.value = err.response?.data?.error || err.response?.data?.message || 'Failed to remove track'
      console.error('Error removing track:', err)
      throw err
    }
  }

  async function fetchBySection(templateId: number | string) {
    isLoading.value = true
    error.value = null
    try {
      const url = targetUserId.value
        ? API_ROUTES.CAMPUS_GUIDE_BY_SECTION(targetUserId.value, templateId)
        : API_ROUTES.CAMPUS_BY_SECTION(templateId)
      const response = await api.get(url)
      const data = response.data as BySectionResponse
      sectionSteps.value[data.template_id] = data.steps
      return data
    } catch (err: any) {
      const d = err.response?.data
      error.value = d?.error || d?.message || 'Failed to fetch section steps'
      console.error('Error fetching section steps:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function updateAutoAdjust(enabled: boolean) {
    const prev = personalCurriculum.value
    if (prev) {
      personalCurriculum.value = { ...prev, auto_adjust_enabled: enabled }
    }
    error.value = null
    try {
      const response = await api.patch(curriculumUrl(), {
        auto_adjust_enabled: enabled,
      })
      personalCurriculum.value = response.data as PersonalCurriculum
      return response.data
    } catch (err: any) {
      if (prev) personalCurriculum.value = prev
      error.value = err?.response?.data?.message || 'Failed to update auto-adjust'
      console.error('Error updating auto-adjust:', err)
      throw err
    }
  }

  async function updateHoursPerWeek(hours: number) {
    const prev = personalCurriculum.value
    if (prev) {
      personalCurriculum.value = { ...prev, hours_per_week: hours }
    }
    error.value = null
    try {
      const response = await api.patch(curriculumUrl(), {
        hours_per_week: hours,
      })
      personalCurriculum.value = response.data as PersonalCurriculum
      // Refetch full plan so steps reflect the redistributed weeks
      await fetchFullPlan()
      return response.data
    } catch (err: any) {
      if (prev) personalCurriculum.value = prev
      error.value = err.response?.data?.hours_per_week?.[0] || err.response?.data?.message || 'Failed to update hours'
      console.error('Error updating hours per week:', err)
      throw err
    }
  }

  async function commitPlan() {
    isCommitting.value = true
    error.value = null
    try {
      const url = targetUserId.value
        ? API_ROUTES.CAMPUS_GUIDE_COMMIT_PLAN(targetUserId.value)
        : API_ROUTES.CAMPUS_COMMIT
      const response = await api.post(url)
      personalCurriculum.value = response.data as PersonalCurriculum
    } catch (err: any) {
      error.value = err.response?.data?.error || err.response?.data?.message || 'Failed to commit plan'
      console.error('Error committing plan:', err)
      throw err
    } finally {
      isCommitting.value = false
    }
  }

  async function reorderSteps(payload: ReorderStepsPayload) {
    isReordering.value = true
    error.value = null
    try {
      const url = targetUserId.value
        ? API_ROUTES.CAMPUS_GUIDE_REORDER_STEPS(targetUserId.value)
        : API_ROUTES.CAMPUS_REORDER_STEPS
      await api.post(url, payload)
      for (const change of payload.changes) {
        const step = planSteps.value.find((s) => s.id === change.step_id)
        if (step) {
          step.week_number = change.week_number
          step.order_in_week = change.order_in_week
        }
      }
    } catch (err: any) {
      const data = err.response?.data
      error.value =
        data?.error ||
        data?.message ||
        (err.response?.status === 503 ? 'Database busy. Please try again.' : 'Failed to reorder steps')
      console.error('Error reordering steps:', err)
      throw err
    } finally {
      isReordering.value = false
    }
  }

  async function addPersonalStep(payload: AddPersonalStepPayload) {
    error.value = null
    try {
      const url = targetUserId.value
        ? API_ROUTES.CAMPUS_GUIDE_ADD_PERSONAL_STEP(targetUserId.value)
        : API_ROUTES.CAMPUS_ADD_PERSONAL_STEP
      const response = await api.post(url, payload)
      const newStep = response.data as PersonalCurriculumStep
      planSteps.value.push(newStep)
      return newStep
    } catch (err: any) {
      const data = err.response?.data
      if (typeof data === 'string') {
        error.value = data
      } else if (data?.error) {
        error.value = data.error
      } else if (data?.message) {
        error.value = data.message
      } else if (data && typeof data === 'object') {
        const firstKey = Object.keys(data)[0]
        const firstVal = data[firstKey]
        error.value = Array.isArray(firstVal) ? firstVal[0] : String(firstVal)
      } else {
        error.value = 'Failed to add personal step'
      }
      console.error('Error adding personal step:', err)
      throw err
    }
  }

  async function deleteStep(id: number | string) {
    error.value = null
    try {
      const url = targetUserId.value
        ? API_ROUTES.CAMPUS_GUIDE_DELETE_FELLOW_STEP(targetUserId.value, id)
        : API_ROUTES.CAMPUS_DELETE_STEP(id)
      await api.delete(url)
      planSteps.value = planSteps.value.filter((s) => s.id !== Number(id))
    } catch (err: any) {
      error.value = err.response?.data?.error || err.response?.data?.message || 'Failed to delete step'
      console.error('Error deleting step:', err)
      throw err
    }
  }

  async function updateGuideStep(stepId: number | string, payload: GuideUpdateStepPayload) {
    if (!targetUserId.value) {
      error.value = 'Not editing a fellow curriculum'
      throw new Error('Not in guide mode')
    }
    error.value = null
    try {
      const { data } = await api.put<PersonalCurriculumStep>(
        API_ROUTES.CAMPUS_GUIDE_UPDATE_STEP(targetUserId.value, stepId),
        payload,
      )
      const idx = planSteps.value.findIndex((s) => s.id === Number(stepId))
      if (idx !== -1) {
        planSteps.value[idx] = { ...planSteps.value[idx], ...data }
      }
      return data
    } catch (err: any) {
      error.value =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to update step'
      console.error('Error updating fellow step:', err)
      throw err
    }
  }

  async function updateProgressStatus(stepId: number | string, newStatus: PersonalProgressStatus) {
    error.value = null
    try {
      await api.put(API_ROUTES.CAMPUS_UPDATE_STEP_STATUS(stepId), { new_status: newStatus })

      const updateStep = (step: PersonalCurriculumStep) => {
        if (!step.progress) {
          step.progress = { status: newStatus, finished_datetime: null }
        } else {
          step.progress.status = newStatus
        }
        if (newStatus === 'DONE') {
          step.progress.finished_datetime = new Date().toISOString()
        }
      }

      const planStep = planSteps.value.find((s) => s.id === Number(stepId))
      if (planStep) updateStep(planStep)

      if (currentWeekData.value) {
        for (const curriculum of currentWeekData.value.curricula) {
          const weekStep = curriculum.steps.find((s) => s.id === Number(stepId))
          if (weekStep) {
            updateStep(weekStep)
            break
          }
        }
      }

      // Refresh curriculum to get updated streak when marking DONE
      if (newStatus === 'DONE') {
        await fetchMyCurriculum()
      }

      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update progress status'
      console.error('Error updating progress status:', err)
      return false
    }
  }

  async function createReview(payload: CreateStepReviewPayload) {
    error.value = null
    try {
      const response = await api.post(API_ROUTES.CAMPUS_CREATE_REVIEW, payload)
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to create review'
      console.error('Error creating review:', err)
      throw err
    }
  }

  async function fetchLatestReview(stepId: number): Promise<StepReview | null> {
    try {
      const response = await api.get(API_ROUTES.CAMPUS_STEP_REVIEW_LATEST(stepId))
      return response.data as StepReview
    } catch (err: any) {
      if (err.response?.status === 404) return null
      console.error('Error fetching latest review:', err)
      throw err
    }
  }

  /** Closed curricula (previous programs) of the current user, or of ``userId`` (guide view). */
  async function fetchPreviousPrograms(userId?: number | string): Promise<PreviousProgramSummary[]> {
    const url = userId
      ? API_ROUTES.CAMPUS_GUIDE_PREVIOUS_PROGRAMS(userId)
      : API_ROUTES.CAMPUS_PREVIOUS_PROGRAMS
    const { data } = await api.get<{ results: PreviousProgramSummary[] }>(url)
    return data.results
  }

  async function fetchStartNewProgramOptions(userId: number | string): Promise<StartNewProgramOptions> {
    const { data } = await api.get<StartNewProgramOptions>(
      API_ROUTES.CAMPUS_MANAGE_START_NEW_PROGRAM(userId),
    )
    return data
  }

  async function startNewProgram(
    userId: number | string,
    payload: StartNewProgramPayload,
  ): Promise<StartNewProgramResponse> {
    const { data } = await api.post<StartNewProgramResponse>(
      API_ROUTES.CAMPUS_MANAGE_START_NEW_PROGRAM(userId),
      payload,
    )
    return data
  }

  function $reset() {
    personalCurriculum.value = null
    planSteps.value = []
    templates.value = []
    currentWeekData.value = null
    sectionSteps.value = {}
    hasPersonalCurriculum.value = null
    isLoading.value = false
    isBuilding.value = false
    isCommitting.value = false
    isReordering.value = false
    error.value = null
    targetUserId.value = null
    clearGuideFellowDisplay()
  }

  return {
    personalCurriculum,
    planSteps,
    templates,
    trackOrder,
    loadTrackOrder,
    saveTrackOrder,
    currentWeekData,
    sectionSteps,
    hasPersonalCurriculum,
    isLoading,
    isBuilding,
    isCommitting,
    isReordering,
    error,
    targetUserId,
    setGuideTarget,
    guideFellowDisplay,
    guideFellowDisplayLoading,
    fetchGuideFellowDisplay,
    clearGuideFellowDisplay,
    isCommitted,
    stepsByWeek,
    totalWeeks,
    weekPointTotals,
    progressByTemplate,
    checkHasPersonalCurriculum,
    fetchTemplates,
    fetchTemplateDetail,
    buildCurriculum,
    fetchMyCurriculum,
    fetchFullPlan,
    fetchCurrentWeek,
    addTrack,
    removeTrack,
    fetchBySection,
    updateAutoAdjust,
    updateHoursPerWeek,
    commitPlan,
    reorderSteps,
    addPersonalStep,
    deleteStep,
    updateGuideStep,
    updateProgressStatus,
    createReview,
    fetchLatestReview,
    fetchPreviousPrograms,
    fetchStartNewProgramOptions,
    startNewProgram,
    $reset,
  }
})
